import { Directive, ElementRef, Input, Renderer2, ViewContainerRef, TemplateRef } from '@angular/core';
import { UserAccessService } from '../services/user-access.service';

@Directive({
	selector: '[evoSecurable]'
})
export class EvoSecurableDirective {
	// readonly display class
	private readonly dispClass = 'doNotDisplay';
	// readonly disabled class
	private readonly disabledClass = 'disabledClass';
	private readonly debug = false;
	/**
	 * Shorthand ID for the current attached element
	 * @returns Uppercase value of the current element ID
	 */
	get id(): string {
		return String(this.el.nativeElement.id).toUpperCase();
	}
	/**
	 * Shorthand for the current attached element
	 * @returns ElementRef.NativeElement
	 */
	get elem(): any {
		return this.el.nativeElement;
	}

	constructor(private aSvc: UserAccessService,
		private el: ElementRef,
		private renderer: Renderer2,
		private _viewContainer: ViewContainerRef,
	) {
		// Guard against no ID defined in the constructor
		this.hideElement();
	}

	/**
	 * To keep consistency of having IDs this will be joined with the ID of the secured element.
	 * @Example Route = 'example/dashboard/drilldowns' id = 'merchantname'
	 * @Example Joined = 'example|dashboard|drilldowns|merchantname'
	 */
	@Input('evoSecurable') evoSecurable: string; //Enables taking a route
	@Input('SEID') salesEntityID: number; //Optional Sales Entity ID for specific permissions
	@Input('MID') merchantID: number; //Optional merchant ID for specific permissions
	@Input('SEC') securableName: string; //Full name (without ESP|) of the securable to use instead of ID
	@Input('MinAccess') minAccessLevel: number; //The minimum access level to display this element (to override default of 1)

	/**
	 * Can't get input values until ngOnInit
	 */
	ngOnInit() {
		if (this.canShow()) {
			// Only try to unhide the field if it has an ID (or explicitly provided sec name)
			this.initializeElement();
		} else {
			// Delete the element
			this.removeElementFromDOM();
		}
	}

	/**
	 * Initializes the element based on securable permission
	 */
	private async initializeElement() {
		let AccessLevel: number;
		if (this.debug) { console.log("************** THE SECURABLE NAME IS: ", this.securableName); }
		if (this.debug) { console.log("************** The ID is: ", this.id); }
		if (this.securableName) {
			this.securableName = this.securableName.toLocaleUpperCase();
			AccessLevel = await this.aSvc.GetAccessLevelBySecurableName(this.securableName, this.evoSecurable, this.salesEntityID, this.merchantID, true);
		} else {
			AccessLevel = await this.aSvc.GetAccessLevelBySecurableName(this.id, this.evoSecurable, this.salesEntityID, this.merchantID, false);
		}
		let threshold = 1;
		if (this.minAccessLevel) {
			threshold = this.minAccessLevel;
		}
		if (AccessLevel >= threshold) { // I don't like this but Bens note said 1 or Greater.
			if (this.debug) { console.log("%cAUTHED ELEMENT: ", 'background:green; color:white;', this.id); }
			this.showElement();
		} else {
			// Element access is 0 or less remove it.
			console.log("%cREMOVED ELEMENT: ", 'background:red; color:white;', this.id, this.securableName, this.evoSecurable);
			this.removeElementFromDOM();
		}
	}

	/**
	 * @returns True if the ID of this element is defined (or the SecurableName is defined).
	 */
	private canShow(): boolean {
		if (!(this.id === '' || this.id === undefined)
			&& (this.securableName === '' || this.securableName === undefined)
			&& !(this.evoSecurable === '')) {
			/**
			 * To help me and others remove dependency on IDs since it creates this 
			 * mess of merging routes and crap together...eventually hope to remove.
			 * Some things like the navigation routes are dependent on it for now.
			 */
			console.warn("%cIT IS STRONGLY DISCOURAGED TO USE IDs ALONE ON SECURABLES " + '\n' +
				"Please revise your [evoSecurable] to also use the [SEC] attribute and specify the securable name excluding 'ESP'" + '\n' +
				"Ex: id='securableName' [evoSecurable] [SEC]='DASHBOARD|securableName'" + '\n' + '\n' +
				'Securable: ', 'background:#ffa200;font-weight:bold; color:black;', this.id);
		}
		if ((this.id === '' || this.id === undefined) && (this.securableName === '' || this.securableName === undefined)) {
			console.warn(this.id, this.securableName);
			console.warn('No ID defined for secured element:', this.el);
			return false;
		}
		return true;
	}

	/**
	 * Hides the current attached element (by adding the display class)
	 */
	private hideElement() {
		this.renderer.addClass(this.elem, this.dispClass);
	}

	/**
	* Shows the current attached element (by removing the display class)
	*/
	private showElement() {
		this.renderer.removeClass(this.elem, this.dispClass);
	}

	/**
	 * Removes the current attached element from the DOM completely
	 */
	private removeElementFromDOM() {
		this.elem.parentNode.removeChild(this.elem);
	}
}
